import { computed, ref, watch } from "@vue/composition-api";
import Vue from "vue";
import { FileIdUpload } from "@scrile/api-provider/dist/api/FileProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useFiles, { UploadFilesData } from "@/hooks/useFiles";
import usePaymentPackage from "@/hooks/usePaymentPackage";
import { getFileMimeType, isAttachmentFile, isInputAttachmentFile } from "@/lib/fileHelpers";
import { calculateProgress } from "@/lib/FileUploader";
import { getMediaThumbnailUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { AttachmentFile, InputAttachmentFile } from "@/types";

export const UID_PREFIX = "uploading_";

const useMessageAttachments = () => {
  const uploading = ref(false);
  const priceList = ref<PaymentPackage[]>([]);
  const selectedPriceId = ref("0");

  const attachedFiles = ref<AttachmentFile[]>([]);
  const privateFiles = computed(() => attachedFiles.value.filter(f => !f.publicAccess));
  const publicFiles = computed(() => attachedFiles.value.filter(f => f.publicAccess));

  const { uploadFilesToS3, stopFileUploading, getUploadUrl } = useFiles();
  const { findAll } = usePaymentPackage();

  const selectPrice = (id: string) => (selectedPriceId.value = id);

  const { authUser } = useAuthUser();
  const isMember = computed(() => authUser.value?.role === config.userRegistrationRole);

  const maxCountPrivateFiles = computed(() =>
    isMember.value ? config.attachments.MAX_FILES_UPLOAD_FOR_MEMBER : config.attachments.MAX_FILES_UPLOAD_FOR_CREATOR
  );
  const maxCountPublicFiles = computed(() => (isMember.value ? 0 : config.attachments.MAX_COVER_FILES));

  const allowedPublicFilesCount = computed(() => maxCountPublicFiles.value - publicFiles.value.length);
  const allowedPrivateFilesCount = computed(() => maxCountPrivateFiles.value - privateFiles.value.length);

  const getLimitedFiles = (files: InputAttachmentFile[]): InputAttachmentFile[] => {
    if (files[0].publicAccess) {
      return files.filter((_, index) => index + 1 <= allowedPublicFilesCount.value);
    } else {
      return files.filter((_, index) => index + 1 <= allowedPrivateFilesCount.value);
    }
  };

  const shapeFilesAndFiledIds = async (files: InputAttachmentFile[]): Promise<FileIdUpload[]> => {
    let fileIds: FileIdUpload[] = [];
    for (const [index, data] of files.entries()) {
      const uploadUrl = await getUploadUrl([{ subjectId: String(index + 1), type: "message_attachment" }]);

      attachedFiles.value[data.publicAccess ? "unshift" : "push"]({
        type: getFileMimeType(data.file),
        uid: UID_PREFIX + uploadUrl[0].fileUpload.uuid,
        src: getMediaThumbnailUrl(data.file, uploadUrl[0].fileUpload.signedUrlPart),
        publicAccess: data.publicAccess,
        uploadProgress: 0,
      });
      fileIds = fileIds.concat(uploadUrl);
    }
    return fileIds;
  };

  const fileIdUpload = ref<FileIdUpload[]>([]);
  const removeFileIdItemByUid = (uid: string): void => {
    fileIdUpload.value = fileIdUpload.value.filter(i => i.fileUpload.uuid !== uid);
  };

  const successFileUpload = (uid: string): void => {
    attachedFiles.value = attachedFiles.value.map(i => {
      if (i.uid.includes(UID_PREFIX + uid)) i.uid = uid;
      return i;
    });
    removeFileIdItemByUid(uid);
  };

  const addInputAttachmentFiles = async (files: InputAttachmentFile[]) => {
    findAll([PaymentPackageEnum.MESSAGE_ATTACHMENT]).then(r => (priceList.value = r));
    if (files.length === 0) return;
    uploading.value = true;

    const limitedFiles: InputAttachmentFile[] = getLimitedFiles(files);
    const filesToUpload: InputAttachmentFile[] = Array.from(limitedFiles);
    fileIdUpload.value = await shapeFilesAndFiledIds(filesToUpload);

    const requestData = fileIdUpload.value.map<UploadFilesData>((fileId, index) => {
      return {
        file: filesToUpload[index].file,
        fileIdUpload: fileId,
        onError: (_, uid) => {
          removeFileIdItemByUid(uid);
        },
        onAbortUploading: removeFileIdItemByUid,
        onSuccess: successFileUpload,
        onUploadProgress: p => {
          const index = attachedFiles.value.findIndex(item => item.uid === UID_PREFIX + fileId.fileUpload.uuid);
          if (index !== -1) {
            Vue.set(attachedFiles.value[index], "uploadProgress", calculateProgress(p.total ?? 0, p?.loaded ?? 0));
          }
        },
      };
    });
    uploadFilesToS3(requestData);
  };

  const isFileNotUnique = (file: AttachmentFile) => {
    return attachedFiles.value.some(f => f.uid === file.uid);
  };
  const addAttachmentFiles = async (files: AttachmentFile[]) => {
    findAll([PaymentPackageEnum.MESSAGE_ATTACHMENT]).then(r => (priceList.value = r));
    files.forEach(file => {
      if (
        (file.publicAccess && allowedPublicFilesCount.value === 0) ||
        (!file.publicAccess && allowedPrivateFilesCount.value === 0) ||
        isFileNotUnique(file)
      ) {
        return;
      }
      attachedFiles.value[file.publicAccess ? "unshift" : "push"](file);
    });
  };

  const addAttachment = async (files: (AttachmentFile | InputAttachmentFile)[]): Promise<void> => {
    const attachmentFiles = files.filter(isAttachmentFile);
    const inputFiles = files.filter(isInputAttachmentFile);
    inputFiles.length > 0 && (await addInputAttachmentFiles(inputFiles));
    attachmentFiles.length > 0 && (await addAttachmentFiles(attachmentFiles));
  };

  const clearAttachments = () => {
    attachedFiles.value = [];
    priceList.value = [];
    selectedPriceId.value = "0";
  };

  const remove = (file?: AttachmentFile) => {
    if (file) {
      stopFileUploading(file.uid);
      removeFileIdItemByUid(file.uid.replace(UID_PREFIX, ""));
      attachedFiles.value = attachedFiles.value.filter(f => f.uid !== file.uid);
      if (selectedPriceId.value !== "0" && privateFiles.value.length === 0 && publicFiles.value.length > 0) {
        clearAttachments();
      }
    } else clearAttachments();
  };

  watch(fileIdUpload, fileIdUpload => {
    uploading.value = !!fileIdUpload.length;
  });

  return {
    uploading,
    priceList,
    attachedFiles,
    hasAttachments: computed(() => attachedFiles.value.length > 0),
    addAttachment,
    remove,
    selectPrice,
    selectedPriceId,
    allowedPublicFilesCount,
    allowedPrivateFilesCount,
  };
};

export default useMessageAttachments;
